import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  Tarjeta,
  TarjetaPagingRes,
  UseMutationsParams,
} from '@/shared/interfaces';
import { getUrlParamsFetch, handleAxiosError } from '@/shared/utils';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchTarjetas = (params?: GetTarjetasParams, enabled = true) => {
  return useQuery({
    queryKey: ['tarjetas', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getTarjetas(params);
    },
    enabled,
  });
};

export const useGetTarjeta = (id: string) => {
  return useQuery({
    queryKey: ['tarjetas', id],
    queryFn: async () => {
      return getTarjeta(id);
    },
  });
};

export const useCreateTarjeta = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createTarjeta,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tarjetas'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Tarjeta creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateTarjeta = (navigate: NavigateFunction, returnUrl: string) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: updateTarjeta,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tarjetas'] });
      navigate(returnUrl);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetTarjetasParams = Partial<Tarjeta> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateTarjetaParams = Omit<Tarjeta, 'id_tarjeta'>;
export type UpdateTarjetaParams = {
  id: string;
  data: CreateTarjetaParams;
};

export const getTarjetas = async (params?: GetTarjetasParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  // Aquí cambiamos TarjetaPagingRes[] por TarjetaPagingRes
  return await get<TarjetaPagingRes>(
    `${urlBackend}api/v1/tarjeta/?${queryParams}`,
    true
  );
};

export const getTarjeta = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<Tarjeta>(`${urlBackend}api/v1/tarjeta/${id}/`, true);

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createTarjeta = async (data: CreateTarjetaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Tarjeta>(`${urlBackend}api/v1/tarjeta/`, data, true);
};

export const updateTarjeta = async ({ id, data }: UpdateTarjetaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Tarjeta>(`${urlBackend}api/v1/tarjeta/${id}/`, data, true);
};
